import React, { useState, useEffect } from 'react';
import { VatSettings } from '../../../types';
import { Input, Button } from '../../atoms';
import './VatSettingsForm.css';

interface VatSettingsFormProps {
  settings: VatSettings | null;
  onSubmit: (settings: Partial<VatSettings>) => void;
  onCancel: () => void;
  loading?: boolean;
}

const VatSettingsForm: React.FC<VatSettingsFormProps> = ({
  settings,
  onSubmit,
  onCancel,
  loading = false
}) => {
  const [formData, setFormData] = useState({
    isEnabled: false,
    percentage: 23,
    isInclusive: false,
    isActive: true
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (settings) {
      setFormData({
        isEnabled: settings.isEnabled,
        percentage: settings.percentage,
        isInclusive: settings.isInclusive,
        isActive: settings.isActive
      });
    }
  }, [settings]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (formData.isEnabled) {
      if (formData.percentage < 0 || formData.percentage > 100) {
        newErrors.percentage = 'VAT percentage must be between 0 and 100';
      }
      
      if (formData.percentage === 0) {
        newErrors.percentage = 'VAT percentage cannot be 0 when VAT is enabled';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    onSubmit(formData);
  };

  const handleToggle = (field: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: !prev[field as keyof typeof prev]
    }));
  };

  return (
    <div className="vat-settings-form">
      <div className="vat-settings-form__header">
        <h4>Configure VAT Settings</h4>
        <p>Set up VAT configuration for your business</p>
      </div>

      <form onSubmit={handleSubmit} className="vat-settings-form__form">
        <div className="vat-settings-form__section">
          <h5>VAT Configuration</h5>
          
          <div className="vat-settings-form__field">
            <div className="toggle-field">
              <div className="toggle-field__content">
                <label className="toggle-field__label">
                  Enable VAT
                </label>
                <p className="toggle-field__description">
                  Enable or disable VAT calculation for all orders
                </p>
              </div>
              <div className="toggle-field__control">
                <button
                  type="button"
                  className={`toggle ${formData.isEnabled ? 'toggle--active' : ''}`}
                  onClick={() => handleToggle('isEnabled')}
                  disabled={loading}
                >
                  <div className="toggle__slider"></div>
                </button>
              </div>
            </div>
          </div>

          {formData.isEnabled && (
            <>
              <div className="vat-settings-form__field">
                <Input
                  label="VAT Percentage"
                  type="number"
                  value={formData.percentage}
                  onChange={(e) => handleInputChange('percentage', parseFloat(e.target.value) || 0)}
                  min={0}
                  max={100}
                  step={0.01}
                  error={errors.percentage}
                  required
                  disabled={loading}
                />
                <p className="field-help">
                  Enter the VAT percentage (e.g., 23 for 23% VAT)
                </p>
              </div>

              <div className="vat-settings-form__field">
                <div className="toggle-field">
                  <div className="toggle-field__content">
                    <label className="toggle-field__label">
                      Inclusive Pricing
                    </label>
                    <p className="toggle-field__description">
                      When enabled, product prices include VAT. When disabled, VAT is added on top of prices.
                    </p>
                  </div>
                  <div className="toggle-field__control">
                    <button
                      type="button"
                      className={`toggle ${formData.isInclusive ? 'toggle--active' : ''}`}
                      onClick={() => handleToggle('isInclusive')}
                      disabled={loading}
                    >
                      <div className="toggle__slider"></div>
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="vat-settings-form__section">
          <h5>System Settings</h5>
          
          <div className="vat-settings-form__field">
            <div className="toggle-field">
              <div className="toggle-field__content">
                <label className="toggle-field__label">
                  Active
                </label>
                <p className="toggle-field__description">
                  Enable or disable the VAT system
                </p>
              </div>
              <div className="toggle-field__control">
                <button
                  type="button"
                  className={`toggle ${formData.isActive ? 'toggle--active' : ''}`}
                  onClick={() => handleToggle('isActive')}
                  disabled={loading}
                >
                  <div className="toggle__slider"></div>
                </button>
              </div>
            </div>
          </div>
        </div>

        {formData.isEnabled && formData.percentage > 0 && (
          <div className="vat-settings-form__preview">
            <h5>Preview</h5>
            <div className="vat-preview">
              <div className="vat-preview__example">
                <div className="vat-preview__row">
                  <span>Subtotal:</span>
                  <span>€100.00</span>
                </div>
                <div className="vat-preview__row">
                  <span>VAT ({formData.percentage}%):</span>
                  <span>€{(100 * formData.percentage / 100).toFixed(2)}</span>
                </div>
                <div className="vat-preview__row vat-preview__row--total">
                  <span>Total:</span>
                  <span>€{(100 + (100 * formData.percentage / 100)).toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="vat-settings-form__actions">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default VatSettingsForm;
